import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { adminApi, chatApi, kbApi, regionsApi, videoApi } from '@carrent/api'
import { useAuthStore } from '@carrent/core'
import { useNavigate } from 'react-router-dom'
import {
  messageTimeLabel,
  ownMessageStatus,
  useConversation,
  useConversations, useVideoSettings } from '@carrent/domain'
import {
  AttachmentThumb,
  ConversationListItem,
  Icon,
  MessageBubble,
  MessageComposer,
  PinnedBar,
} from '@carrent/ui'

const uuid = () =>
  globalThis.crypto?.randomUUID?.() ?? `c-${Date.now()}-${Math.random().toString(36).slice(2)}`

/**
 * Чаты в консоли.
 *
 * Три колонки — плотный рабочий инструмент, не растянутый мобильный
 * список: слева диалоги с фильтрами и массовой отправкой, в центре
 * лента, справа — участники, закреплённое и все вложения диалога
 * («ту накладную от вторника» диспетчер ищет здесь, а не скроллом).
 *
 * Консоль онлайн-first: отправка идёт напрямую, но с тем же
 * client_message_id — контракт идемпотентности один для всех.
 */

// ─────────────────────────────────────────────────────────────────
//  Левая колонка
// ─────────────────────────────────────────────────────────────────

function ConversationsPane({
  items, loading, activeId, onOpen,
  massMode, massSelected, onToggleMass, onToggleSelect,
  onCreate, filters, setFilters, regions,
}) {
  const filtered = useMemo(() => {
    let list = items
    if (filters.q) {
      const q = filters.q.toLowerCase()
      list = list.filter((c) => (c.title || '').toLowerCase().includes(q))
    }
    if (filters.unreadOnly) list = list.filter((c) => (c.unread_count || 0) > 0)
    if (filters.regionId) {
      list = list.filter((c) => String(c.region_id) === String(filters.regionId))
    }
    return list
  }, [items, filters])

  return (
    <aside className="chats__list">
      <div className="chats__list-tools">
        <input
          className="input"
          placeholder="Поиск диалога"
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
        />
        <div className="chats__list-filters">
          <select
            className="input"
            value={filters.regionId}
            onChange={(e) => setFilters((f) => ({ ...f, regionId: e.target.value }))}
            aria-label="Фильтр по региону"
          >
            <option value="">Все регионы</option>
            {regions.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          <label className="chats__unread-filter">
            <input
              type="checkbox"
              checked={filters.unreadOnly}
              onChange={(e) =>
                setFilters((f) => ({ ...f, unreadOnly: e.target.checked }))}
            />
            Непрочитанные
          </label>
        </div>
        <div className="chats__list-actions">
          <button type="button" className="btn btn--primary" onClick={onCreate}>
            <Icon name="plus" size={16} /> Группа
          </button>
          <button
            type="button"
            className={`btn ${massMode ? 'btn--primary' : 'btn--secondary'}`}
            onClick={onToggleMass}
            title="Одно сообщение в несколько групп"
          >
            Массовая отправка
          </button>
        </div>
      </div>

      {loading && <div className="chats__list-state"><span className="spinner" /></div>}

      {!loading && filtered.length === 0 && (
        <div className="chats__list-state text-muted">Диалогов нет</div>
      )}

      <div className="chats__list-items">
        {filtered.map((conv) => (
          <div key={conv.id} className="chats__row">
            {massMode && conv.kind === 'group' && (
              <input
                type="checkbox"
                className="chats__row-check"
                aria-label={`Выбрать ${conv.title}`}
                checked={massSelected.includes(conv.id)}
                onChange={() => onToggleSelect(conv.id)}
              />
            )}
            <ConversationListItem
              conversation={conv}
              title={conv.title || 'Личный диалог'}
              subtitle={
                conv.last_message?.deleted
                  ? 'Сообщение удалено'
                  : conv.last_message?.body || ''
              }
              timeLabel={messageTimeLabel(conv.last_message_at)}
              active={String(conv.id) === String(activeId)}
              onClick={() => onOpen(conv.id)}
            />
          </div>
        ))}
      </div>
    </aside>
  )
}

// ─────────────────────────────────────────────────────────────────
//  Правая панель
// ─────────────────────────────────────────────────────────────────

function DetailsPane({ conversationId, details, pinned, isOwner, onChanged }) {
  const [attachments, setAttachments] = useState([])
  const [urls, setUrls] = useState({})
  const [addQuery, setAddQuery] = useState('')
  const [candidates, setCandidates] = useState([])

  useEffect(() => {
    if (!conversationId) return
    chatApi
      .conversationAttachments(conversationId)
      .then(({ data }) => setAttachments(data.data.items))
      .catch(() => setAttachments([]))
  }, [conversationId, details?.last_message_at])

  // Ленивая подгрузка presigned-миниатюр.
  useEffect(() => {
    attachments.slice(0, 24).forEach((a) => {
      if (urls[a.id] !== undefined) return
      chatApi
        .attachmentUrl(a.id, 'thumb')
        .then(({ data }) => setUrls((u) => ({ ...u, [a.id]: data.data.url })))
        .catch(() => setUrls((u) => ({ ...u, [a.id]: null })))
    })
  }, [attachments]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!addQuery) {
      setCandidates([])
      return
    }
    const t = setTimeout(() => {
      adminApi
        .listUsers({ q: addQuery, per_page: 8 })
        .then(({ data }) => setCandidates(data.data.items || []))
        .catch(() => {})
    }, 250)
    return () => clearTimeout(t)
  }, [addQuery])

  if (!details) return null
  const memberIds = new Set((details.members || []).map((m) => m.user_id))

  const addMember = async (userId) => {
    await chatApi.updateConversation(conversationId, { add_member_ids: [userId] })
    setAddQuery('')
    onChanged()
  }
  const removeMember = async (userId) => {
    await chatApi.updateConversation(conversationId, { remove_member_ids: [userId] })
    onChanged()
  }
  const unpin = async (messageId) => {
    await chatApi.unpin(messageId)
    onChanged()
  }

  return (
    <aside className="chats__details">
      <section>
        <h3 className="chats__details-title">
          Участники · {details.members?.length || 0}
        </h3>
        <ul className="chats__members">
          {(details.members || []).map((m) => (
            <li key={m.user_id} className="chats__member">
              <span className="chats__member-name">
                {m.full_name || m.user_id}
                {m.role === 'owner' && (
                  <span className="chats__member-role"> · владелец</span>
                )}
              </span>
              {isOwner && m.role !== 'owner' && (
                <button
                  type="button"
                  className="chats__member-remove"
                  aria-label={`Исключить ${m.full_name}`}
                  onClick={() => removeMember(m.user_id)}
                >
                  <Icon name="close" size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
        {isOwner && details.kind === 'group' && (
          <div className="chats__add-member">
            <input
              className="input"
              placeholder="Добавить участника"
              value={addQuery}
              onChange={(e) => setAddQuery(e.target.value)}
            />
            {candidates
              .filter((u) => !memberIds.has(u.id))
              .map((u) => (
                <button
                  key={u.id}
                  type="button"
                  className="chats__candidate"
                  onClick={() => addMember(u.id)}
                >
                  <Icon name="plus" size={14} /> {u.full_name} · {u.phone}
                </button>
              ))}
          </div>
        )}
      </section>

      {pinned.length > 0 && (
        <section>
          <h3 className="chats__details-title">Закреплённое</h3>
          {pinned.map((p) => (
            <PinnedBar
              key={p.id}
              pinned={p}
              onOpen={() => {}}
              onUnpin={isOwner ? () => unpin(p.message_id) : undefined}
            />
          ))}
        </section>
      )}

      <section>
        <h3 className="chats__details-title">Вложения · {attachments.length}</h3>
        {attachments.length === 0 && (
          <p className="text-muted chats__details-empty">Файлов пока нет</p>
        )}
        <div className="chats__attachments">
          {attachments.map((a) => (
            <AttachmentThumb
              key={a.id}
              url={urls[a.id] ?? null}
              mime={a.mime}
              error={urls[a.id] === null}
              onClick={() =>
                chatApi.attachmentUrl(a.id).then(({ data }) =>
                  globalThis.open(data.data.url, '_blank', 'noopener'))}
            />
          ))}
        </div>
      </section>
    </aside>
  )
}

// ─────────────────────────────────────────────────────────────────
//  Создание группы
// ─────────────────────────────────────────────────────────────────

function CreateGroupModal({ regions, onClose, onCreated }) {
  const [title, setTitle] = useState('')
  const [regionId, setRegionId] = useState('')
  const [q, setQ] = useState('')
  const [found, setFound] = useState([])
  const [selected, setSelected] = useState([]) // [{id, full_name}]
  const [kbGroups, setKbGroups] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    kbApi.groups().then(({ data }) => setKbGroups(data.data.items || data.data || []))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!q) return setFound([])
    const t = setTimeout(() => {
      adminApi.listUsers({ q, per_page: 8 })
        .then(({ data }) => setFound(data.data.items || []))
        .catch(() => {})
    }, 250)
    return () => clearTimeout(t)
  }, [q])

  const add = (u) =>
    setSelected((cur) => (cur.some((s) => s.id === u.id) ? cur : [...cur, u]))
  const remove = (id) => setSelected((cur) => cur.filter((s) => s.id !== id))

  const fromKbGroup = async (groupId) => {
    if (!groupId) return
    const { data } = await kbApi.groupMembers(groupId)
    const members = data.data.items || data.data || []
    members.forEach((m) =>
      add({ id: m.user_id || m.id, full_name: m.full_name || m.user?.full_name }))
  }

  const pickRegion = (id) => {
    setRegionId(id)
    const region = regions.find((r) => String(r.id) === String(id))
    if (region && !title) setTitle(`Регион — ${region.name}`)
  }

  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      const { data } = await chatApi.createGroup({
        title,
        memberIds: selected.map((s) => s.id),
        regionId: regionId || null,
      })
      onCreated(data.data)
    } catch (e) {
      setError(e?.response?.data?.message || 'Не удалось создать группу')
      setBusy(false)
    }
  }

  return (
    <div className="cmdk-overlay" role="presentation" onClick={onClose}>
      <div
        className="cmdk-box chats__modal"
        role="dialog"
        aria-label="Новая группа"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="chats__modal-title">Новая группа</h2>

        <label className="chats__field">
          Название
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Регион — Юг"
            autoFocus
          />
        </label>

        <div className="chats__field-row">
          <label className="chats__field">
            Регион
            <select
              className="input"
              value={regionId}
              onChange={(e) => pickRegion(e.target.value)}
            >
              <option value="">Без привязки</option>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </label>

          <label className="chats__field">
            Из группы БЗ
            <select
              className="input"
              defaultValue=""
              onChange={(e) => fromKbGroup(e.target.value)}
            >
              <option value="">Добавить участников…</option>
              {kbGroups.map((g) => (
                <option key={g.id} value={g.id}>{g.name || g.title}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="chats__field">
          Участники
          <input
            className="input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск по имени или телефону"
          />
        </label>
        {found.map((u) => (
          <button
            key={u.id}
            type="button"
            className="chats__candidate"
            onClick={() => add(u)}
          >
            <Icon name="plus" size={14} /> {u.full_name} · {u.phone}
          </button>
        ))}

        {selected.length > 0 && (
          <div className="chats__chips">
            {selected.map((s) => (
              <span key={s.id} className="chats__chip">
                {s.full_name}
                <button
                  type="button"
                  aria-label={`Убрать ${s.full_name}`}
                  onClick={() => remove(s.id)}
                >
                  <Icon name="close" size={12} />
                </button>
              </span>
            ))}
          </div>
        )}

        {error && <p className="chats__error">{error}</p>}

        <div className="chats__modal-actions">
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!title.trim() || busy}
            onClick={submit}
          >
            Создать
          </button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
//  Кнопка «Позвонить»
// ─────────────────────────────────────────────────────────────────

/**
 * Создаёт звонок, привязанный к диалогу, и уводит в него. Ссылка
 * попадает в диалог системным сообщением (это делает сервер) —
 * участники видят звонок там, где идёт разговор.
 *
 * Кнопки нет, если модуль выключен, интеграция с чатом выключена
 * или роль не может создавать звонки, — а не задизейблена: серые
 * кнопки в шапке чата вызывают вопросы чаще, чем их отсутствие.
 */
function CallButton({ conversationId, title }) {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const { settings, canCreate } = useVideoSettings()
  const [busy, setBusy] = useState(false)

  if (!settings.enabled || !settings.call_from_chat || !canCreate(user?.role)) {
    return null
  }

  const start = async () => {
    setBusy(true)
    try {
      const { data } = await videoApi.create({
        title: title ? `Звонок: ${title}` : 'Звонок из чата',
        privacy: 'open',
        conversation_id: conversationId,
      })
      navigate(`/admin/video/${data.data.id}`)
    } catch {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      className="btn btn--ghost"
      onClick={start}
      disabled={busy}
      title="Начать видеозвонок с участниками диалога"
    >
      {busy ? <span className="spinner spinner--sm" /> : <Icon name="chat" size={14} />}
      Позвонить
    </button>
  )
}

// ─────────────────────────────────────────────────────────────────
//  Экран
// ─────────────────────────────────────────────────────────────────

export default function AdminChatsPage() {
  const user = useAuthStore((s) => s.user)
  const { items, loading, refresh } = useConversations()
  const [activeId, setActiveId] = useState(null)
  const [filters, setFilters] = useState({ q: '', regionId: '', unreadOnly: false })
  const [regions, setRegions] = useState([])
  const [creating, setCreating] = useState(false)
  const [massMode, setMassMode] = useState(false)
  const [massSelected, setMassSelected] = useState([])
  const [massProgress, setMassProgress] = useState(null)
  const [detailsOpen, setDetailsOpen] = useState(true)

  const {
    messages, pinned, details, loading: feedLoading, error: feedError,
    hasMore, loadOlder, appendLocal, markRead, reload,
  } = useConversation(activeId, { enabled: Boolean(activeId) })

  useEffect(() => {
    regionsApi
      .list()
      .then(({ data }) => setRegions(data.data.items || data.data || []))
      .catch(() => {
        // Фильтр по региону просто не покажется — не критично.
      })
  }, [])

  const lastServerId = messages.length ? messages[messages.length - 1].id : null
  const feedRef = useRef(null)
  useEffect(() => {
    feedRef.current?.scrollTo?.(0, feedRef.current.scrollHeight)
    if (lastServerId && activeId) markRead(lastServerId)
  }, [messages.length, lastServerId, activeId, markRead])

  const myMember = details?.members?.find((m) => m.user_id === user?.id)
  const isOwner = myMember?.role === 'owner' || user?.role === 'admin'

  const send = useCallback(
    async ({ body }) => {
      const fd = new FormData()
      fd.append('body', body)
      fd.append('client_message_id', uuid())
      const { data } = await chatApi.sendMessage(activeId, fd)
      appendLocal(data.data)
    },
    [activeId, appendLocal]
  )

  const massSend = useCallback(
    async ({ body }) => {
      setMassProgress({ done: 0, total: massSelected.length })
      for (const [i, convId] of massSelected.entries()) {
        const fd = new FormData()
        fd.append('body', body)
        fd.append('client_message_id', uuid())
        // Последовательно: сбой одной группы виден по прогрессу,
        // а не топит остальные.
        // eslint-disable-next-line no-await-in-loop
        await chatApi.sendMessage(convId, fd).catch(() => {})
        setMassProgress({ done: i + 1, total: massSelected.length })
      }
      setMassMode(false)
      setMassSelected([])
      setTimeout(() => setMassProgress(null), 2000)
      refresh()
    },
    [massSelected, refresh]
  )

  const pinMessage = async (m) => {
    await chatApi.pin(m.id)
    reload()
  }
  const saveMessage = (m) => chatApi.save(m.id)

  return (
    <div className="chats">
      <ConversationsPane
        items={items}
        loading={loading}
        activeId={activeId}
        onOpen={setActiveId}
        massMode={massMode}
        massSelected={massSelected}
        onToggleMass={() => {
          setMassMode((v) => !v)
          setMassSelected([])
        }}
        onToggleSelect={(id) =>
          setMassSelected((cur) =>
            cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id])}
        onCreate={() => setCreating(true)}
        filters={filters}
        setFilters={setFilters}
        regions={regions}
      />

      <main className="chats__feed-col">
        {massMode ? (
          <div className="chats__mass">
            <p>
              Выбрано групп: <b>{massSelected.length}</b>. Сообщение уйдёт
              в каждую отдельным постом.
            </p>
            {massProgress && (
              <p className="text-muted">
                Отправлено {massProgress.done} из {massProgress.total}
              </p>
            )}
            <MessageComposer
              onSend={massSend}
              submitOnEnter
              disabled={massSelected.length === 0}
              disabledHint="Выберите группы слева"
              placeholder="Сообщение во все выбранные группы"
            />
          </div>
        ) : !activeId ? (
          <div className="chats__placeholder">
            <Icon name="chat" size={40} />
            <p className="text-muted">Выберите диалог слева</p>
          </div>
        ) : (
          <>
            <header className="chats__feed-header">
              <span className="chats__feed-title">
                {details?.title || 'Диалог'}
              </span>
              <CallButton conversationId={activeId} title={details?.title} />
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setDetailsOpen((v) => !v)}
              >
                {detailsOpen ? 'Скрыть панель' : 'Панель'}
              </button>
            </header>

            <PinnedBar pinned={pinned[0]} />

            <div className="chats__feed" ref={feedRef}>
              {feedLoading && <span className="spinner" />}
              {!feedLoading && feedError && (
                <div className="chats__placeholder">
                  <p className="text-muted">{feedError}</p>
                  <button type="button" className="btn btn--secondary" onClick={reload}>
                    Повторить
                  </button>
                </div>
              )}
              {hasMore && (
                <button
                  type="button"
                  className="btn btn--ghost chats__older"
                  onClick={loadOlder}
                >
                  Показать более ранние
                </button>
              )}
              {messages.map((m) => {
                const own = m.sender_id === user?.id
                return (
                  <div key={m.client_message_id || m.id} className="chats__msg">
                    <MessageBubble
                      message={m}
                      own={own}
                      senderName={!own ? m.sender?.full_name : ''}
                      timeLabel={messageTimeLabel(m.created_at)}
                      status={own ? ownMessageStatus(m, details?.members, user?.id) : null}
                    />
                    {m.kind === 'text' && !m.deleted && (
                      <span className="chats__msg-actions">
                        {isOwner && (
                          <button
                            type="button"
                            aria-label="Закрепить"
                            title="Закрепить"
                            onClick={() => pinMessage(m)}
                          >
                            <Icon name="pin" size={14} />
                          </button>
                        )}
                        <button
                          type="button"
                          aria-label="Сохранить"
                          title="В сохранённое"
                          onClick={() => saveMessage(m)}
                        >
                          <Icon name="bookmark" size={14} />
                        </button>
                      </span>
                    )}
                  </div>
                )
              })}
            </div>

            <MessageComposer onSend={send} submitOnEnter placeholder="Сообщение" />
          </>
        )}
      </main>

      {activeId && detailsOpen && !massMode && (
        <DetailsPane
          conversationId={activeId}
          details={details}
          pinned={pinned}
          isOwner={isOwner}
          onChanged={reload}
        />
      )}

      {creating && (
        <CreateGroupModal
          regions={regions}
          onClose={() => setCreating(false)}
          onCreated={(conv) => {
            setCreating(false)
            refresh()
            setActiveId(conv.id)
          }}
        />
      )}
    </div>
  )
}
